import { useCallback, useEffect, useRef, useState } from "react";
import { useHistory, useParams, useLocation } from "react-router-dom";
import {
  AlertTriangle,
  ClipboardList,
  Eye,
  Pencil,
  Plus,
  Settings,
  SprayCan,
  Trash2,
} from "lucide-react";
import {
  IonPage,
  IonHeader,
  IonToolbar,
  IonContent,
  IonButtons,
  IonBackButton,
} from "@ionic/react";
import { useStore } from "../store/useStore";
import type { ITask, IArea } from "../types";
import SortableTaskList from "../components/SortableTaskList";
import { ProgressBar } from "../components/ProgressBar";
import { EmptyState } from "../components/EmptyState";
import { RoomIcon } from "../components/ui/RoomIcon";
import { Button } from "../components/ui/Button";
import { Card } from "../components/ui/Card";
import { Skeleton, SkeletonList } from "../components/ui/Skeleton";
import { cn } from "../lib/utils";
import { useDialogA11y } from "../hooks/useDialogA11y";

// Task Cleanliness Card Component (for overview mode)
const TaskCleanlinessCard = ({
  task,
  dirtyPercentage,
  onTaskTap,
}: {
  task: ITask;
  dirtyPercentage: number;
  onTaskTap: (taskId: string) => void;
}) => {
  // Difficulty dots (effort, max 3 shown)
  const totalDots = 3;
  const filledDots = Math.min(task.points, totalDots);

  const lastCompleted = task.last_completed_at
    ? new Date(task.last_completed_at).toLocaleDateString("en-US", {
        weekday: "long",
        day: "numeric",
        month: "long",
      })
    : "Never";

  return (
    <button
      type="button"
      onClick={() => onTaskTap(task.uuid)}
      className="w-full text-left card-surface p-5 min-h-[44px] transition-all duration-200 hover:border-primary/30 active:scale-[0.98] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
    >
      <span className="flex items-start justify-between gap-3 mb-2">
        <span className="text-foreground font-bold text-lg leading-snug">{task.name}</span>
        <span
          className="flex gap-1 pt-2 shrink-0"
          role="img"
          aria-label={`Effort ${task.points} of 5`}
        >
          {Array.from({ length: totalDots }, (_, index) => (
            <span
              key={index}
              className={cn(
                "w-2 h-2 rounded-full",
                index < filledDots ? "bg-primary" : "bg-surface-raised"
              )}
            />
          ))}
        </span>
      </span>

      <span className="block text-muted-foreground text-sm mb-3">
        <span className="block">Last cleanup: {lastCompleted}</span>
        <span className="block">
          Every {task.frequency_number} {task.frequency_unit}
        </span>
      </span>

      <ProgressBar percentage={dirtyPercentage} />

      {task.description && (
        <span className="block text-muted-foreground text-sm mt-3">{task.description}</span>
      )}
    </button>
  );
};

export default function AreaDetailPage() {
  const history = useHistory();
  const location = useLocation();
  const { areaId } = useParams<{ areaId: string }>();

  // Check if we're in edit mode
  const urlParams = new URLSearchParams(location.search);
  const isEditMode = urlParams.get("mode") === "edit";

  // State for delete confirmation
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const closeDeleteConfirm = useCallback(() => setShowDeleteConfirm(false), []);
  const deleteDialogFocusRef = useRef<HTMLButtonElement>(null);
  useDialogA11y(showDeleteConfirm, closeDeleteConfirm, deleteDialogFocusRef);

  // Get data from Zustand store
  const currentHouseholdId = useStore((state) => state.currentHouseholdId);
  const areas = useStore((state) => state.areas);
  const tasks = useStore((state) => state.tasks);
  const cleanlinessMap = useStore((state) => state.cleanlinessMap);
  const loading = useStore((state) => state.loading);
  const setCurrentHousehold = useStore((state) => state.setCurrentHousehold);
  const userHouseholds = useStore((state) => state.userHouseholds);
  const deleteArea = useStore((state) => state.deleteArea);

  useEffect(() => {
    if (!currentHouseholdId && userHouseholds.length > 0) {
      setCurrentHousehold(userHouseholds[0].household_uuid);
    }
  }, [userHouseholds, currentHouseholdId, setCurrentHousehold]);

  // Find the current area
  const currentArea = areas.find((area: IArea) => area.uuid === areaId);

  // Filter tasks for this area
  const areaTasks = tasks.filter((task) => task.area_uuid === areaId);

  const handleEditTask = (taskId: string) => {
    history.push(`/areas/${areaId}/tasks/${taskId}?mode=edit`);
  };

  const handleTaskTap = (taskId: string) => {
    // In cleanliness mode, navigate to task detail view
    history.push(`/areas/${areaId}/tasks/${taskId}?mode=view`);
  };

  const toggleMode = () => {
    const newMode = isEditMode ? "view" : "edit";
    history.push(`/areas/${areaId}?mode=${newMode}`);
  };

  const handleDelete = async () => {
    setIsDeleting(true);
    try {
      await deleteArea(areaId);
      history.push('/home');
    } catch (error) {
      console.error("Error deleting area:", error);
      setIsDeleting(false);
    }
  };

  const isLoading = loading.tasks || loading.areas;
  const areaName = currentArea?.name || "Area";

  const avgDirty =
    areaTasks.length > 0
      ? areaTasks.reduce(
          (sum: number, task: ITask) => sum + (cleanlinessMap[task.uuid] ?? 0),
          0
        ) / areaTasks.length
      : 0;
  const urgentCount = areaTasks.filter(
    (task: ITask) => (cleanlinessMap[task.uuid] ?? 0) >= 90
  ).length;
  const cleanCount = areaTasks.filter(
    (task: ITask) => (cleanlinessMap[task.uuid] ?? 0) < 60
  ).length;

  return (
    <IonPage>
      <IonHeader className="ion-no-border">
        <IonToolbar>
          <IonButtons slot="start">
            <IonBackButton defaultHref="/home" text="" />
          </IonButtons>
          <IonButtons slot="end">
            <Button
              variant={isEditMode ? "soft" : "secondary"}
              size="sm"
              onClick={toggleMode}
              leadingIcon={
                isEditMode ? (
                  <Eye className="w-4 h-4" aria-hidden="true" />
                ) : (
                  <Pencil className="w-4 h-4" aria-hidden="true" />
                )
              }
              className="mr-2 min-h-[40px]"
            >
              {isEditMode ? "Switch to View" : "Switch to Edit"}
            </Button>
          </IonButtons>
        </IonToolbar>
      </IonHeader>
      <IonContent>
        <div className="max-w-lg mx-auto px-4 pt-2 pb-28">
          {/* Area header */}
          <div className="flex items-center gap-4 mb-6">
            {isLoading ? (
              <Skeleton className="w-16 h-16 rounded-3xl" />
            ) : (
              <RoomIcon name={currentArea?.name} size="lg" />
            )}
            <div className="min-w-0">
              <h1 className="text-[28px] leading-tight font-extrabold tracking-tight text-foreground truncate">
                {areaName}
              </h1>
              <p className="text-muted-foreground text-sm">
                {areaTasks.length} task{areaTasks.length !== 1 ? "s" : ""}
                {isEditMode ? " (Edit Mode)" : " (View Mode)"}
              </p>
            </div>
          </div>

          {isLoading ? (
            <SkeletonList rows={4} />
          ) : (
            <>
              {/* Mode Description */}
              <div className="flex items-start gap-3 rounded-2xl bg-surface p-4 mb-6">
                <span
                  aria-hidden="true"
                  className="inline-flex items-center justify-center w-9 h-9 rounded-full bg-card text-muted-foreground shrink-0"
                >
                  {isEditMode ? <Settings className="w-5 h-5" /> : <SprayCan className="w-5 h-5" />}
                </span>
                <div>
                  <h2 className="text-foreground font-bold">
                    {isEditMode ? "Edit Mode" : "View Mode"}
                  </h2>
                  <p className="text-muted-foreground text-sm">
                    {isEditMode
                      ? "Edit task templates, reorder tasks, and manage area structure."
                      : "View task cleanliness status and tap tasks to see details. See what needs attention first."}
                  </p>
                </div>
              </div>

              {/* Content based on mode */}
              {isEditMode ? (
                <>
                  {areaTasks.length > 0 ? (
                    <>
                      <div className="flex items-center justify-between mb-4">
                        <h3 className="section-title">Task Templates</h3>
                        <div className="text-muted-foreground text-sm">
                          Drag & drop to reorder
                        </div>
                      </div>
                      <SortableTaskList tasks={areaTasks} onEdit={handleEditTask} />
                      <Button
                        variant="secondary"
                        block
                        size="lg"
                        className="mt-6"
                        leadingIcon={<Plus className="w-5 h-5" aria-hidden="true" />}
                        onClick={() => history.push(`/areas/${areaId}/tasks/create`)}
                      >
                        Add New Task
                      </Button>
                    </>
                  ) : (
                    <EmptyState
                      icon={ClipboardList}
                      title="No tasks yet"
                      description="Add tasks to this area to get started"
                      action={{
                        label: "Add First Task",
                        onClick: () => history.push(`/areas/${areaId}/tasks/create`),
                      }}
                    />
                  )}

                  {/* Delete Area Button (only in edit mode) */}
                  <div className="mt-8 pt-8 border-t border-border">
                    <Button
                      variant="danger"
                      block
                      size="lg"
                      leadingIcon={<Trash2 className="w-4 h-4" aria-hidden="true" />}
                      onClick={() => setShowDeleteConfirm(true)}
                    >
                      Delete Area
                    </Button>
                  </div>
                </>
              ) : (
                <>
                  {areaTasks.length > 0 ? (
                    <>
                      {/* Area Cleanliness Summary */}
                      <Card padding="lg" className="mb-6">
                        <h3 className="section-title mb-4">Area Summary</h3>
                        <div className="grid grid-cols-3 gap-4 items-start">
                          <div>
                            <ProgressBar percentage={avgDirty} />
                          </div>
                          <div className="text-center">
                            <div className="text-2xl font-extrabold text-red-500 dark:text-red-400">
                              {urgentCount}
                            </div>
                            <div className="text-muted-foreground text-sm">Urgent</div>
                          </div>
                          <div className="text-center">
                            <div className="text-2xl font-extrabold text-emerald-600 dark:text-emerald-400">
                              {cleanCount}
                            </div>
                            <div className="text-muted-foreground text-sm">Clean</div>
                          </div>
                        </div>
                      </Card>

                      {/* Tasks List */}
                      <h3 className="section-title mb-3">Outstanding Tasks</h3>
                      <ul className="space-y-3">
                        {areaTasks.map((task) => (
                          <li key={task.uuid}>
                            <TaskCleanlinessCard
                              task={task}
                              dirtyPercentage={cleanlinessMap[task.uuid] ?? 0}
                              onTaskTap={handleTaskTap}
                            />
                          </li>
                        ))}
                      </ul>
                    </>
                  ) : (
                    <EmptyState
                      icon={SprayCan}
                      title="No tasks to clean"
                      description="Switch to edit mode to add tasks to this area"
                      action={{ label: "Switch to Edit", onClick: toggleMode }}
                    />
                  )}
                </>
              )}
            </>
          )}

          {/* Delete Confirmation Modal */}
          {showDeleteConfirm && (
            <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-6 z-50">
              <div
                role="alertdialog"
                aria-modal="true"
                aria-labelledby="delete-area-title"
                className="bg-card rounded-3xl p-6 max-w-sm w-full shadow-sheet"
              >
                <h3 id="delete-area-title" className="text-lg font-extrabold text-foreground mb-2">
                  Delete Area?
                </h3>
                <p className="text-muted-foreground mb-4">
                  This will permanently delete "{currentArea?.name}" and all {areaTasks.length} task{areaTasks.length !== 1 ? 's' : ''} in this area.
                </p>
                <p className="text-sm font-medium text-amber-700 dark:text-amber-400 mb-6 flex items-center gap-1.5">
                  <AlertTriangle className="w-4 h-4 shrink-0" aria-hidden="true" /> This action cannot be undone.
                </p>
                <div className="flex gap-3">
                  <Button
                    ref={deleteDialogFocusRef}
                    variant="secondary"
                    className="flex-1"
                    onClick={closeDeleteConfirm}
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={handleDelete}
                    loading={isDeleting}
                    className="flex-1 bg-destructive text-destructive-foreground hover:bg-destructive/90 shadow-none"
                  >
                    {isDeleting ? "Deleting..." : "Delete"}
                  </Button>
                </div>
              </div>
            </div>
          )}
        </div>
      </IonContent>
    </IonPage>
  );
}
